import type { Cents } from "./money";
import type { CostType } from "./proposedCostLine";
import type { QuantityUnit } from "./feature";

/**
 * Assembly system (Phase 4) — the AEGIS-like core that converts a Project
 * Feature into deterministic, explainable Proposed Cost Lines.
 *
 *   Assembly Template  --COPY-->  Project Assembly Instance
 *        (library)                 (owns its options/components)
 *                                        |
 *                                  deterministic engine
 *                                        |
 *                             Generated Proposed Cost Lines
 *
 * Copy-on-use isolation: editing a template never changes an instance and
 * vice versa. The template link remains for provenance and suggestions.
 *
 * QUANTITY MODEL (approved architectural adjustment): assemblies compute
 * CONSTRUCTION REQUIREMENTS (e.g. "935 sqft of LVP required"), never
 * retailer package quantities. Product package sizing/ceil() belongs to
 * the future Material Catalog / Procurement system. Estimating unit costs
 * are construction-unit rates ($/sqft, $/each, $/lf, $/hour).
 *
 * All numbers are exact integers:
 *   quantities        ×100      (hundredths)
 *   multipliers       ×10000    (4 decimals)
 *   waste / tax rates ×100      (hundredths of a percent; 10% -> 1000)
 *   money             cents
 */

/** Component type maps directly onto the estimate's Cost Type. */
export type ComponentType = CostType; // 'material' | 'labor' | 'other'

export type ComponentRole = "primary" | "ancillary" | "demolition";

export type ComponentInclusion = "required" | "conditional" | "optional";

export type QuantityBasis = "fixed" | "per-unit";

export const COMPONENT_ROLES: ReadonlyArray<{ id: ComponentRole; displayName: string }> = [
  { id: "primary", displayName: "Primary" },
  { id: "ancillary", displayName: "Ancillary" },
  { id: "demolition", displayName: "Demolition" },
];

export const COMPONENT_INCLUSIONS: ReadonlyArray<{ id: ComponentInclusion; displayName: string }> =
  [
    { id: "required", displayName: "Required" },
    { id: "conditional", displayName: "Conditional (option)" },
    { id: "optional", displayName: "Optional" },
  ];

/** A yes/no question the assembly asks (no expression language). */
export interface AssemblyOption {
  id: string;
  prompt: string;
  /** Template: the default answer. Instance: the current answer. */
  value: boolean;
  sortOrder: number;
}

/** Shared shape of template components and (copied) instance components. */
export interface AssemblyComponentCore {
  id: string;
  name: string;
  componentType: ComponentType;
  role: ComponentRole;
  inclusion: ComponentInclusion;
  /** For conditional inclusion: the option that controls it. */
  conditionOptionId: string | null;
  /** For optional inclusion: template default / instance current state. */
  included: boolean;
  qtyBasis: QuantityBasis;
  /** fixed basis: the quantity itself (×100). */
  fixedQtyX100: number;
  /** per-unit basis: driving qty × multiplier (×10000). */
  multiplierX10000: number;
  /** material waste, hundredths of a percent (10% -> 1000). */
  wastePctX100: number;
  unit: QuantityUnit;
  /** Estimating unit cost per construction unit, integer cents. */
  unitCostCents: Cents;
  /**
   * Duration-based labor estimating (spec §20A, Phase 9A): estimated labor
   * duration PER UNIT in hundredths of a minute (6h → 36000). When set on a
   * labor component, its cost derives from ceil-to-whole-hours(total
   * duration) × the configured company labor rate — an estimating
   * assumption, never a quote. Null = dollar-priced (fixed assumptions and
   * contractor quotes remain first-class).
   */
  laborMinutesX100: number | null;
  taxable: boolean;
  categoryId: string | null;
  /** Generic material this component requires (Phase 6); keeps assemblies retailer-neutral. */
  materialDefinitionId: string | null;
  notes: string;
  sortOrder: number;
}

export interface AssemblyTemplate {
  id: string;
  displayName: string;
  /** The construction unit the assembly consumes (its driving quantity). */
  drivingUnit: QuantityUnit;
  notes: string;
  isSystem: boolean;
  sortOrder: number;
  archivedAt: string | null;
  options: AssemblyOption[];
  components: AssemblyComponentCore[];
  /** Optional template default product per component (copy-on-use at attach). */
  defaultProductIds?: Record<string, string | null>;
}

export interface AssemblyInstanceComponent extends AssemblyComponentCore {
  instanceId: string;
  /** Non-destructive quantity override (×100); calculated value preserved. */
  qtyOverrideX100: number | null;
  /** Where this price came from ('manual' in Phase 4; catalog etc. later). */
  priceSource: string;
  archivedAt: string | null;
}

export interface AssemblyInstance {
  id: string;
  projectFeatureId: string;
  /** Provenance link; null = custom project assembly. */
  templateId: string | null;
  name: string;
  drivingUnit: QuantityUnit;
  notes: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  options: AssemblyOption[];
  components: AssemblyInstanceComponent[];
}

/**
 * Resolve whether a component is currently part of the estimate.
 * required -> always; conditional -> its option's answer; optional -> the
 * user's included toggle. Archived (removed) components are never included.
 */
export function componentIncluded(
  component: Pick<
    AssemblyInstanceComponent,
    "inclusion" | "conditionOptionId" | "included" | "archivedAt"
  >,
  optionsById: Map<string, AssemblyOption>,
): boolean {
  if (component.archivedAt !== null) return false;
  switch (component.inclusion) {
    case "required":
      return true;
    case "conditional":
      return component.conditionOptionId !== null
        ? (optionsById.get(component.conditionOptionId)?.value ?? false)
        : false;
    case "optional":
      return component.included;
  }
}

export function validateComponentCore(c: {
  name: string;
  fixedQtyX100: number;
  multiplierX10000: number;
  wastePctX100: number;
  unitCostCents: number;
}): string[] {
  const problems: string[] = [];
  if (c.name.trim() === "") {
    problems.push("Component name is required.");
  }
  for (const [label, value, min] of [
    ["Fixed quantity", c.fixedQtyX100, 0],
    ["Multiplier", c.multiplierX10000, 0],
    ["Waste", c.wastePctX100, 0],
  ] as const) {
    if (!Number.isSafeInteger(value) || value < min) {
      problems.push(`${label} must be a valid non-negative number.`);
    }
  }
  if (!Number.isSafeInteger(c.unitCostCents)) {
    problems.push("Unit cost must be a valid amount.");
  }
  return problems;
}
